import { UserAr } from './userAr';
import { User } from './user';
import { getHash } from '../../language/module';
import {Resort} from '../../resortDetails/resort';
import {equals} from 'ramda';
import {CRUD_STATE} from '../../forms/elements/crudButtons/crudState';

const ALL_RESORTS_ID = 0;

/**
 * Convert a User object to a UserAr object. This mainly just strips off all the noisy properties, but also supports
 * password changes by way of the UserAr.password property.
 */
export const toUserAr = (user: User): UserAr => ({
  userName:       user.userName,
  password:       user.passwordHash,
  email:          user.email,
  firstName:      user.firstName,
  lastName:       user.lastName,
  role:           user.role,
  resortLocations: user.resortLocations,
  resetPassword:  user.resetPassword,
  userId:         user.userId,
  hasEmail:       user.hasEmail,
  blocked:        user.blocked,
  lastLogin:      user.lastLogin,
});


/**
 * Applies the properties from a UserAr object onto a User object. This is good for taking updates from the API and
 * applying them to app state without performing another fetch.
 */
export const updateUser = (toUpdate: User, updateWith: UserAr): User => ({
  ...toUpdate,
  userName:       updateWith.userName,
  firstName:      updateWith.firstName,
  lastName:       updateWith.lastName,
  email:          updateWith.email,
  passwordHash:   getHash(updateWith.password).toString(), // Not cryptographic
  role:           updateWith.role,
  resortLocations: updateWith.resortLocations,
  resetPassword:  updateWith.resetPassword,
  hasEmail:       updateWith.hasEmail,
  blocked:        updateWith.blocked,
  lastLogin:      updateWith.lastLogin,
});


/**
 * Fabricates a complete User object from the given UserAr object. Useful for mapping new users into app state without
 * performing a new fetch.
 */
export const toUser = (user: UserAr): User => ({
  subjectId:            '',
  firstName:            user.firstName,
  lastName:             user.lastName,
  role:                 user.role,
  resortLocations:      user.resortLocations,
  userId:               user.userId,
  userName:             user.userName,
  normalizedUserName:   user.userName,
  email:                user.email,
  normalizedEmail:      '',
  emailConfirmed:       false,
  passwordHash:         getHash(user.password).toString(), // Not cryptographic
  securityStamp:        '',
  concurrencyStamp:     '',
  phoneNumber:          '000-000-0000',
  phoneNumberConfirmed: false,
  twoFactorEnabled:     false,
  lockoutEnd:           '',
  lockoutEnabled:       false,
  accessFailedCount:    0,
  resetPassword:        user.resetPassword,
  hasEmail:             user.hasEmail,
  blocked:              user.blocked,
  lastLogin:            user.lastLogin,
});

export type SelectOptions = {value: string, label: string}[]


export const generateResortOptions = (resorts: Resort[]): SelectOptions => {
const resortOptions: SelectOptions = [];
  resorts.forEach((resort: Resort) => {
    resortOptions.push({value: resort.resortId.toString(), label: resort.resortName});
  });
  return resortOptions.sort((a, b) => a.label > b.label ? 1 : -1);
}


export const generateOptionsFromIds = (locationIds: number[], allResorts: Resort[]) => {
  const locations = allResorts.filter((resort: Resort) => locationIds.includes(resort.resortId));
  return generateResortOptions(locations);
}

export const mapIdsToResortAbbrevs = (locationIds: number[], allResorts: Resort[]) => {
  const locations = allResorts.filter((resort: Resort) => locationIds.includes(resort.resortId)).sort((a, b) => a.resortAbbreviation > b.resortAbbreviation ? 1 : -1);
  return locations.map((resort: Resort) => resort.resortAbbreviation).join(', ');
}

export const mapIdsToResorts = (locationIds: number[], allResorts: Resort[]) => {
  const locations = allResorts.filter((resort: Resort) => locationIds.includes(resort.resortId)).sort((a, b) => a.resortName > b.resortName ? 1 : -1);
  return locations;
}

export const hasAllResorts = (locationIds: number[], allResorts: Resort[]): boolean => {
  const locations = allResorts.filter((resort: Resort) => !locationIds.includes(resort.resortId) && resort.resortId !== ALL_RESORTS_ID);
  return locations.length === 0;
}

/**
 * Checks if the user has changed while defaulting props user pw to empty string
 * UserId also gets set to the userUpdate userId as this is not a property that can be changed
 * @param propsUser
 * @param userUpdate
 */
export const checkIfUserChanged = (propsUser: UserAr, userUpdate: UserAr): boolean => {
  const defaultPropsPw = {
    userName: propsUser.userName,
    firstName: propsUser.firstName,
    lastName: propsUser.lastName,
    email: propsUser.email,
    password: '',
    role: propsUser.role,
    resortLocations: propsUser.resortLocations,
    resetPassword: propsUser.resetPassword,
    userId: userUpdate.userId,
  }

  return equals(defaultPropsPw, userUpdate);
}

/**
 * Returns the CRUD state based on the isCreateRow and isEditing props
 * @param isCreateRow
 * @param isEditing
 */
export const crudStateFor = (isCreateRow: boolean, isEditing: boolean): CRUD_STATE => {
  if (isCreateRow) {
    return CRUD_STATE.CREATING;
  }

  if (isEditing) {
    return CRUD_STATE.UPDATING;
  }

  return CRUD_STATE.READING;
};


